import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthPromptModal from '../components/AuthPromptModal';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { translateBatch } from '../services/translationService';
import useSeo from '../hooks/useSeo';
import { breadcrumbJsonLd, SITE_URL } from '../lib/seoSchemas';
import Header from '../components/Header';
import Footer from '../components/Footer';
import KmsChatHistorySidebar from '../components/KmsChatHistorySidebar';
import KmsChatHistoryView from '../components/KmsChatHistoryView';
import { Button } from '../components/ui/button';
import {
  Wheat, Home, GraduationCap, Briefcase, Star,
  ArrowRight, Loader2, Sparkles, ChevronRight, Target,
  Menu
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UI_STRINGS = {
  aiPowered: "AI-Powered Policy Analysis",
  title: "KMS Smart",
  subtitle: "Describe yourself in a few words and discover how government policies personally benefit you",
  step1: "Describe Yourself",
  placeholder: "Example: I am a small farmer with 2 acres of land in Andhra Pradesh, growing paddy and vegetables. My annual income is around ₹1.5 lakh.",
  characters: "characters",
  tryExample: "Try an example:",
  step2: "Select Policy Area",
  step3: "Choose Language & Analyse",
  analysing: "Analysing...",
  analyseBtn: "Analyse with KMS Smart",
  errorProfile: "Please describe yourself to get a personalized analysis.",
  errorPolicy: "Please select a policy area to analyze.",
  errorGeneric: "Something went wrong. Please try again later.",
  errorTimeout: "Analysis is taking longer than expected. Please try again with a shorter description.",
  loadingTitle: "KMS Smart is Analysing Your Profile",
  loadingText: "Our AI is matching you with relevant schemes and calculating potential benefits...",
};

const ICON_MAP = {
  wheat: Wheat,
  home: Home,
  'graduation-cap': GraduationCap,
  briefcase: Briefcase,
  star: Star,
};

const COLOR_MAP = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', iconBg: 'bg-emerald-100', badge: 'bg-emerald-500' },
  blue: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', iconBg: 'bg-sky-100', badge: 'bg-sky-500' },
  purple: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', iconBg: 'bg-violet-100', badge: 'bg-violet-500' },
  orange: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconBg: 'bg-amber-100', badge: 'bg-amber-500' },
  pink: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', iconBg: 'bg-rose-100', badge: 'bg-rose-500' },
};

const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
];

const EXAMPLE_PROFILES = [
  "I am a small farmer with 2 acres in Andhra Pradesh growing paddy",
  "I am a 22-year-old female engineering student from Hyderabad",
  "I am a woman running a small tailoring business in Tamil Nadu",
  "I am a daily wage worker from Bihar with 3 children",
];

export default function ImpactAnalyser() {
  useSeo({
    title: 'KMS Smart — AI-Powered Government Policy Impact Analysis | KnowMySchemes',
    description: 'Describe yourself in a few words and discover which Indian government schemes benefit you most. AI-powered, multi-language, free to try.',
    canonical: 'https://knowmyschemes.com/impact-analyser',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'KMS Smart',
        alternateName: 'KnowMySchemes Smart Policy Impact Analyser',
        url: `${SITE_URL}/impact-analyser`,
        applicationCategory: 'GovernmentApplication',
        operatingSystem: 'Web',
        description:
          'AI-powered tool that takes a short profile description and returns personalised analysis of how Indian government schemes and recent policies impact you.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
        inLanguage: ['en-IN', 'hi-IN', 'te-IN', 'ta-IN'],
        publisher: { '@type': 'Organization', name: 'KnowMySchemes', url: SITE_URL },
      },
      breadcrumbJsonLd([
        { name: 'Home', url: `${SITE_URL}/` },
        { name: 'KMS Smart', url: `${SITE_URL}/impact-analyser` },
      ]),
    ],
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const { t, language: appLanguage } = useLanguage();
  const ui = useTranslatedUI(UI_STRINGS);
  const [profileText, setProfileText] = useState('');
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(appLanguage || 'en');
  const [policyEvents, setPolicyEvents] = useState([]);
  const [translatedPolicies, setTranslatedPolicies] = useState([]);
  const [translatedExamples, setTranslatedExamples] = useState(EXAMPLE_PROFILES);
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState('');
  const [progressMessage, setProgressMessage] = useState('');

  // Chat-history sidebar state — read-only viewer for past analyses
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [activeAnalysisId, setActiveAnalysisId] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const viewMode = activeAnalysisId ? 'history' : 'new';

  const handleSelectHistory = (analysisId) => {
    setActiveAnalysisId(analysisId);
    setMobileHistoryOpen(false);
    // Scroll the main column to top so the thread is immediately visible
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
  };

  const handleStartNewChat = () => {
    setActiveAnalysisId(null);
    setMobileHistoryOpen(false);
    // Nudge the refresh key so the sidebar rehydrates after user has run
    // a brand-new analysis in the next session.
    setHistoryRefreshKey((k) => k + 1);
    setProfileText('');
    setError('');
  };

  useEffect(() => {
    setSelectedLanguage(appLanguage || 'en');
  }, [appLanguage]);

  useEffect(() => {
    fetchPolicyEvents();
  }, []);

  // Auto-select policy areas based on keywords in the user's profile text.
  // Removes the confusing manual "Select Policy Area" step — users no longer
  // need to guess which policy bucket their profile fits into.
  useEffect(() => {
    if (!policyEvents.length) return;
    const text = (profileText || '').toLowerCase();
    if (!text.trim()) {
      // Empty profile → pre-select all so backend has full scope on first run
      setSelectedPolicies(policyEvents.map(e => e.id));
      return;
    }
    // Keyword map — matched against profile text to pick the right policy cards.
    const POLICY_KEYWORDS = {
      budget:     ['farmer', 'farm', 'agri', 'crop', 'paddy', 'rice', 'wheat', 'village', 'rural', 'land', 'kisan', 'drone', 'cattle', 'dairy'],
      housing:    ['house', 'housing', 'home', 'shelter', 'rent', 'awas', 'pmay', 'slum'],
      education:  ['student', 'school', 'college', 'study', 'studying', 'education', 'tuition', 'scholarship', 'university', 'engineering', 'exam', 'graduate', 'pursu'],
      startup:    ['business', 'startup', 'entrepreneur', 'shop', 'trader', 'msme', 'tailor', 'weaver', 'artisan', 'self employ', 'udyam', 'mudra', 'small enterprise'],
      women:      ['woman', 'women', 'girl', 'mother', 'widow', 'mahila', 'daughter', 'bride', 'pregnant', 'ujjwala', 'female', 'she '],
    };
    const matched = new Set();
    policyEvents.forEach(ev => {
      const haystack = `${ev.id} ${ev.title} ${ev.subtitle || ''}`.toLowerCase();
      // 1) If profile mentions policy-specific keyword → select
      for (const [tag, kws] of Object.entries(POLICY_KEYWORDS)) {
        const tagMatchesPolicy = haystack.includes(tag) ||
          kws.some(k => haystack.includes(k));
        if (tagMatchesPolicy && kws.some(k => text.includes(k))) {
          matched.add(ev.id);
          break;
        }
      }
    });
    // Fallback: if nothing matched (user typed something unrelated), select all
    setSelectedPolicies(matched.size > 0 ? Array.from(matched) : policyEvents.map(e => e.id));
  }, [profileText, policyEvents]);

  // Translate examples when app language changes
  useEffect(() => {
    if (appLanguage === 'en') {
      setTranslatedExamples(EXAMPLE_PROFILES);
      return;
    }
    translateBatch(EXAMPLE_PROFILES, appLanguage)
      .then(setTranslatedExamples)
      .catch(() => setTranslatedExamples(EXAMPLE_PROFILES));
  }, [appLanguage]);

  // Translate policy cards when app language changes
  useEffect(() => {
    if (policyEvents.length === 0) return;
    if (appLanguage === 'en') {
      setTranslatedPolicies(policyEvents);
      return;
    }
    const allTexts = policyEvents.flatMap(e => [e.title, e.subtitle, e.badge]);
    translateBatch(allTexts, appLanguage).then(translated => {
      let idx = 0;
      setTranslatedPolicies(policyEvents.map(e => ({
        ...e,
        title: translated[idx++] || e.title,
        subtitle: translated[idx++] || e.subtitle,
        badge: translated[idx++] || e.badge,
      })));
    }).catch(() => setTranslatedPolicies(policyEvents));
  }, [appLanguage, policyEvents]);

  const fetchPolicyEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/impact-analyser/policy-events`);
      const data = await res.json();
      if (data.success) {
        setPolicyEvents(data.events);
      }
    } catch (err) {
      console.error('Failed to fetch policy events:', err);
      // Use hardcoded fallback
      setPolicyEvents([
        { id: 'union_budget_2025', title: 'Union Budget 2025-26', subtitle: 'Agriculture & Rural Development', icon: 'wheat', badge: 'LIVE', color: 'green' },
        { id: 'pm_awas_yojana', title: 'PM Awas Yojana 2.0', subtitle: 'Housing for All', icon: 'home', badge: 'NEW', color: 'blue' },
        { id: 'education_policy', title: 'National Education Policy', subtitle: 'Scholarships & Skills', icon: 'graduation-cap', badge: 'LIVE', color: 'purple' },
        { id: 'startup_msme', title: 'Startup India & MSME', subtitle: 'Business Support', icon: 'briefcase', badge: 'NEW', color: 'orange' },
        { id: 'women_empowerment', title: 'Women Empowerment Schemes', subtitle: 'Self Help Groups & Support', icon: 'star', badge: 'LIVE', color: 'pink' },
      ]);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    // Non-signed-up users see the auth popup before analysis runs
    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }
    if (!profileText.trim()) {
      setError(ui.errorProfile);
      return;
    }
    if (selectedPolicies.length === 0) {
      setError(ui.errorPolicy);
      return;
    }
    setError('');
    setLoading(true);
    setProgressMessage(ui.analysing || 'Analysing your profile…');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      const res = await fetch(`${API_URL}/api/impact-analyser/analyze-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_text: profileText,
          policy_event: selectedPolicies,
          language: selectedLanguage,
          user_id: user?.id || null,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) throw new Error('Analysis failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let resultData = null;
      let streamErr = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.event === 'progress' && ev.message) {
              setProgressMessage(ev.message);
            } else if (ev.event === 'result') {
              resultData = ev.data;
            } else if (ev.event === 'error') {
              streamErr = ev.message || 'Analysis failed';
            }
          } catch (_e) {
            // Ignore malformed NDJSON line — stream pipeline is best-effort per chunk
            console.debug('analyze-stream: dropping malformed line');
          }
        }
        if (resultData || streamErr) break;
      }
      clearTimeout(timeoutId);

      if (streamErr) {
        setError(streamErr);
        return;
      }
      if (resultData && resultData.success) {
        navigate('/impact-analyser/results', { state: { analysisData: resultData, profileText, selectedLanguage } });
      } else {
        setError(ui.errorGeneric || 'Analysis failed. Please try again.');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      if (err.name === 'AbortError') {
        setError(ui.errorTimeout);
      } else {
        setError(ui.errorGeneric);
      }
    } finally {
      setLoading(false);
      setProgressMessage('');
    }
  };

  const fillExample = (text) => {
    setProfileText(text);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {/* Hero — compact; full width above the sidebar/main split */}
      <section className="relative bg-gradient-to-br from-green-700 via-green-600 to-teal-600 text-white overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 py-5 sm:py-6 text-center">
          <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[11px] font-medium">{ui.aiPowered}</span>
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight" data-testid="impact-hero-title">
            {ui.title}
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-green-100 max-w-xl mx-auto leading-relaxed">
            {ui.subtitle}
          </p>
        </div>
      </section>

      {/* Sidebar + main column start here, below the hero. Page scrolls as a whole so the
          site-wide Footer stays at the bottom. Sidebar is sticky so it stays in view while
          users scroll the main chat column. */}
      <div className="flex flex-1">
        {/* Left: chat-history sidebar — desktop persistent, flush against left edge.
            Sticky below the header so it tracks with page scroll but never covers the footer. */}
        {isAuthenticated && (
          <KmsChatHistorySidebar
            userId={user?.id}
            activeAnalysisId={activeAnalysisId}
            onSelect={handleSelectHistory}
            onStartNew={handleStartNewChat}
            isOpen={mobileHistoryOpen}
            onClose={() => setMobileHistoryOpen(false)}
            refreshKey={historyRefreshKey}
          />
        )}

        {/* Main column — fills full remaining width, scrolls with the page (no internal scroll) */}
        <main className="flex-1 min-w-0 pb-20 md:pb-0">
          {/* Content column — fills the main area from sidebar to right edge */}
          <div className="px-4 sm:px-6 lg:px-10 pt-6 relative z-10 pb-12">
            <div className="space-y-8">
              {/* Mobile-only: toggle to open history drawer */}
              {isAuthenticated && (
                <div className="lg:hidden flex items-center justify-between pt-2">
                  <button
                    onClick={() => setMobileHistoryOpen(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm active:scale-95 transition"
                    data-testid="open-history-mobile-btn"
                  >
                    <Menu className="h-3.5 w-3.5" />
                    History
                  </button>
                  {viewMode === 'history' && (
                    <button
                      onClick={handleStartNewChat}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5"
                      data-testid="start-new-chat-mobile-btn"
                    >
                      New chat
                    </button>
                  )}
                </div>
              )}

              {viewMode === 'history' ? (
                <KmsChatHistoryView
                  analysisId={activeAnalysisId}
                  onStartNew={handleStartNewChat}
                  onBack={() => setMobileHistoryOpen(true)}
                />
              ) : (
                <>
                  {/* Describe Yourself — single unified step (analyse button nested inside) */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8" data-testid="profile-input-section">
                    <div className="flex items-center gap-3 mb-5">
                      <Sparkles className="w-5 h-5 text-green-600" />
                      <h2 className="text-lg font-semibold text-gray-900">{ui.step1}</h2>
                    </div>
                    <textarea
                      data-testid="profile-text-input"
                      value={profileText}
                      onChange={(e) => { setProfileText(e.target.value); setError(''); }}
                      placeholder={ui.placeholder}
                      className="w-full h-32 sm:h-36 p-4 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none transition-all text-sm sm:text-base"
                      maxLength={500}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-400">{profileText.length}/500 {ui.characters}</p>
                    </div>

                    {/* Auto-matched policy badge — shows which buckets the AI will analyse */}
                    {profileText.trim().length > 4 && selectedPolicies.length > 0 && selectedPolicies.length < policyEvents.length && (
                      <div
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-800 animate-in fade-in slide-in-from-bottom-1 duration-300"
                        data-testid="auto-matched-policies"
                      >
                        <Target className="h-3 w-3 text-emerald-700 flex-shrink-0" />
                        <span>
                          <span className="font-semibold">Matched to: </span>
                          {policyEvents
                            .filter(e => selectedPolicies.includes(e.id))
                            .map(e => e.title.replace(/\s*\d{4}.*/, '').trim())
                            .join(' · ')}
                        </span>
                      </div>
                    )}

                    {/* Example profiles */}
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">{ui.tryExample}</p>
                      <div className="flex flex-wrap gap-2">
                        {translatedExamples.map((ex, i) => (
                          <button
                            key={i}
                            data-testid={`example-profile-${i}`}
                            onClick={() => fillExample(EXAMPLE_PROFILES[i])}
                            className="text-xs bg-gray-100 hover:bg-green-50 hover:text-green-700 text-gray-600 px-3 py-1.5 rounded-full border border-gray-200 hover:border-green-300 transition-colors"
                          >
                            {ex.length > 50 ? ex.slice(0, 50) + '...' : ex}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Analyse button — moved inline so the form feels like a single action */}
                    <div className="mt-6 pt-5 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
                      <Button
                        data-testid="analyze-button"
                        onClick={handleAnalyze}
                        disabled={loading || !profileText.trim() || selectedPolicies.length === 0}
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-600/20 transition-all w-full sm:w-auto"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {ui.analysing}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            {ui.analyseBtn}
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>
                      {error && (
                        <p className="text-sm text-red-600 font-medium flex-1" data-testid="analysis-error">{error}</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Loading Overlay — shared across both views */}
              {loading && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="loading-overlay">
                  <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center shadow-2xl">
                    <div className="relative w-16 h-16 mx-auto mb-5">
                      <div className="absolute inset-0 border-4 border-green-200 rounded-full" />
                      <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin" />
                      <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{ui.loadingTitle}</h3>
                    <p className="text-sm text-gray-600 min-h-[20px] transition-opacity" data-testid="loading-progress">
                      {progressMessage || ui.loadingText}
                    </p>
                    <div className="mt-4 flex justify-center gap-1">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Site-wide footer — always at page bottom, consistent with rest of site */}
      <div className="hidden md:block">
        <Footer />
      </div>

      {/* Auth Prompt Modal — shown when non-signed-up users click Analyse */}
      <AuthPromptModal
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
      />
    </div>
  );
}
